import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveHomeReturn } from '@/utils/homeReturnStore';

export default function Projects() {
  const cardRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = cardRef.current;
    if (!el) return;
    if (window.matchMedia('(hover: none)').matches) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    const rx = (-py * 2.4).toFixed(2);
    const ry = (px * 2.6).toFixed(2);
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
    el.style.boxShadow = `${(-px * 14).toFixed(1)}px ${(14 + py * 6).toFixed(1)}px 32px -12px rgba(20,20,20,0.18)`;
  };

  const handleLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transform = '';
    el.style.boxShadow = '';
  };

  const handleCardClick = () => {
    saveHomeReturn(window.scrollY, 'projects');
    navigate('/applications');
  };

  return (
    <section id="projects" className="py-[72px] md:py-[144px] bg-solar-bg">
      <div className="container-x">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <p className="text-xs font-medium text-solar-green tracking-wider uppercase">Projects</p>
            <h2 className="display-font mt-4 text-[34px] md:text-[56px] font-semibold leading-[1.02] text-solar-ink">
              Real roofs.<br/>Real numbers.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-solar-muted text-[15px] leading-relaxed">
              A quiet portfolio of homes, offices and mixed-use properties running on Sunfield systems.
            </p>
          </div>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {/* Large left */}
          <div className="col-span-12 md:col-span-7 flex flex-col">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-solar-divider">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20luxury%20canyon%20residence%20with%20flat%20rooftop%20solar%20panels%2C%20warm%20wood%20and%20white%20architecture%2C%20desert%20landscaping%20with%20native%20plants%2C%20golden%20hour%20soft%20natural%20light%2C%20editorial%20architecture%20photography%2C%20calm%20premium%20quiet%20composition%2C%20cool%20neutral%20warm%20tones&width=1000&height=750&seq=proj-canyon-01&orientation=landscape"
                alt="Canyon Residence"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-5 flex items-baseline justify-between">
              <h3 className="display-font text-[20px] md:text-[24px] font-semibold text-solar-ink">Canyon Residence</h3>
              <p className="text-solar-muted text-sm">Residential · 12.8 kW</p>
            </div>

            {/* Editorial accent block filling the gap */}
            <button
              ref={cardRef}
              type="button"
              onClick={handleCardClick}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
              className="mt-8 flex-1 rounded-2xl bg-[#FFC905] p-8 md:p-10 flex flex-col justify-between gap-6 group cursor-pointer will-change-transform transition-[transform,box-shadow] duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-solar-ink focus-visible:ring-offset-4 focus-visible:ring-offset-solar-bg active:scale-[0.995] text-left border-0"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <p className="display-font text-[22px] md:text-[28px] font-semibold leading-[1.15] text-solar-ink">
                Built around real places.
              </p>
              <div className="flex items-center justify-between">
                <p className="text-[13px] md:text-sm font-medium tracking-wider uppercase text-solar-ink/80">
                  Residential · Commercial · Hospitality
                </p>
                <i className="ri-arrow-right-line text-solar-ink text-lg transition-transform duration-300 group-hover:translate-x-1"></i>
              </div>
            </button>
          </div>

          {/* Right column split */}
          <div className="col-span-12 md:col-span-5 flex flex-col gap-8">
            <div>
              <div className="aspect-[3/2] w-full overflow-hidden rounded-2xl bg-solar-divider">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20low%20rise%20commercial%20office%20building%20with%20rooftop%20solar%20panel%20array%2C%20light%20facade%20with%20wood%20accents%2C%20subtle%20landscaping%2C%20bright%20natural%20daylight%2C%20clear%20sky%2C%20editorial%20architecture%20photography%2C%20calm%20clean%20minimal%20composition&width=800&height=534&seq=proj-northloop-01&orientation=landscape"
                  alt="North Loop Offices"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-5 flex items-baseline justify-between">
                <h3 className="display-font text-[20px] md:text-[24px] font-semibold text-solar-ink">North Loop Offices</h3>
                <p className="text-solar-muted text-sm">Commercial · 94 kW</p>
              </div>
            </div>
            <div>
              <div className="aspect-[3/2] w-full overflow-hidden rounded-2xl bg-solar-divider">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20contemporary%20suburban%20family%20home%20with%20black%20solar%20panels%20cleanly%20integrated%20on%20sloped%20roof%2C%20white%20siding%20and%20dark%20window%20frames%2C%20green%20lawn%20and%20mature%20trees%2C%20bright%20natural%20daylight%2C%20editorial%20architecture%20photography%2C%20quiet%20premium%20composition&width=800&height=534&seq=proj-linden-01&orientation=landscape"
                  alt="Linden Family Home"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-5 flex items-baseline justify-between">
                <h3 className="display-font text-[20px] md:text-[24px] font-semibold text-solar-ink">Linden Home</h3>
                <p className="text-solar-muted text-sm">Residential · 8.4 kW</p>
              </div>
            </div>
          </div>

          {/* Full width bottom */}
          <div className="col-span-12">
            <div className="aspect-[4/3] md:aspect-[16/7] w-full overflow-hidden rounded-2xl bg-solar-divider">
              <img
                src="https://readdy.ai/api/search-image?query=large%20modern%20hospitality%20hotel%20building%20with%20extensive%20rooftop%20solar%20panel%20array%2C%20light%20architectural%20facade%2C%20subtle%20coastal%20landscaping%2C%20bright%20natural%20daylight%2C%20clear%20sky%2C%20editorial%20architecture%20photography%2C%20calm%20quiet%20premium%20composition%2C%20cool%20neutral%20tones&width=1600&height=700&seq=proj-harbor-01&orientation=landscape"
                alt="Harbor View Resort"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-5 flex items-baseline justify-between">
              <h3 className="display-font text-[20px] md:text-[24px] font-semibold text-solar-ink">Harbor View Resort</h3>
              <p className="text-solar-muted text-sm">Hospitality · 312 kW</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}